/**
 * Single source of truth for the trust & confidentiality copy.
 *
 * Idea-theft fear is the product's #1 conversion blocker. This module holds
 * every piece of trust language so the hero ledger, the protection panel, the
 * cursor-anchored note, and the static idea-capture blurbs all speak in exactly
 * the same words. See @brand/voice.md ("Disclosure and confidentiality copy")
 * for the voice rules, and web.md ("Trust & confidentiality system").
 *
 * The governing rule: brass marks proof. A brass-underlined phrase always links
 * to the fuller explanation; brass is never decorative here.
 */

// ---------------------------------------------------------------------------
// Hero trust ledger — three short claims under the primary CTA.
// ---------------------------------------------------------------------------

export interface TrustPillar {
  /** Full claim text. */
  text: string;
  /**
   * If present, this substring of `text` is rendered with the brass underline
   * and links to the protection panel. Only one pillar should carry a link
   * (brass stays rare and meaningful).
   */
  linkPhrase?: string;
}

export const TRUST_PILLARS: TrustPillar[] = [
  { text: 'Never shared' },
  { text: 'You own it' },
  { text: 'Real USPTO-registered attorneys', linkPhrase: 'USPTO-registered attorneys' },
];

// ---------------------------------------------------------------------------
// Protection panel — the dedicated landing section, written as mechanism.
// ---------------------------------------------------------------------------

export const PROTECTION_EYEBROW = 'How we protect your idea';
export const PROTECTION_HEADING = "What happens to your idea, and what doesn't.";
export const PROTECTION_SUBHEAD =
  'Not a privacy promise. The actual mechanism, point by point.';

/**
 * The closing of the protection panel — placed AFTER the five mechanisms, so it
 * lands once the idea-theft fear is already neutralized, then RELOCATES the risk
 * from theft to inaction. Three tiers, designed so small→large contrast IS the
 * drama (the prominent, hard-to-scroll-past version): a quiet italic SETUP
 * (concede the instinct), a BIG Sentient PAYOFF (the statement, "unbuilt" in
 * brass — the brand's gravity/value color, sober finality not a momentum CTA),
 * then a small mechanism RECEIPT that keeps the reframe safe (the brass link to
 * the Training row preserves "brass = proof you can click"). The competitor
 * stays abstract (the unbuilt idea), never a named thief. Distinct from the hero
 * subhead's "rarely lost to theft" reframe so the page never repeats a line.
 */
export const PROTECTION_CLOSING = {
  setup: 'The safest place for an idea feels like your own head.',
  payoffBefore: 'The only real risk is leaving it ',
  payoffAccent: 'unbuilt.',
  receiptBefore: 'Nothing here is ',
  receiptLink: 'used to train AI',
  receiptAfter: ', and you can delete it anytime.',
};

export interface ProtectionPoint {
  serial: string;
  label: string;
  /** The claim line, set in serif. May contain a single brass token (see brassToken). */
  claim: string;
  /** A word/number inside `claim` to render in brass (e.g. "100%"). */
  brassToken?: string;
  /** The mechanism sentence, set in sans. Omitted for the ownership row. */
  mechanism?: string;
  /** The ownership row renders this as an inline Sentient italic pull-quote. */
  ownershipClause?: string;
  /** The "what we touch" row renders this one-way data diagram in mono. */
  flow?: { reads: string; toward: string; blocked: string };
}

export const PROTECTION_POINTS: ProtectionPoint[] = [
  {
    serial: '01',
    label: 'Training',
    claim: 'Never used to train AI.',
    mechanism:
      'A contractual commitment in our terms, not a preference you have to find and switch off. Your idea is never added to any training set, ours or anyone else\u2019s.',
  },
  {
    serial: '02',
    label: 'Indexing',
    claim: 'Never indexed or searchable.',
    mechanism:
      'It exists in no search index, internal or external. No one can find it by browsing or querying, and it is never surfaced in anyone else\u2019s results.',
  },
  {
    serial: '03',
    label: 'Human access',
    claim: 'Never seen by a human, unless you ask.',
    mechanism:
      'No one at Me\u2019Invent reads it. It is processed only by the system that builds your results \u2014 until you explicitly request a practitioner connection and reach a USPTO-registered attorney.',
  },
  {
    serial: '04',
    label: 'Ownership',
    claim: 'You own 100% of it.',
    brassToken: '100%',
    ownershipClause:
      'You own everything you create here. Every idea you capture, every refinement, and every memo we generate belongs to you, the inventor. We take no license, no assignment, and no claim of any kind.',
  },
  {
    serial: '05',
    label: 'What we touch',
    claim: 'Public data, read-only.',
    mechanism:
      'We research your idea by searching public patent, prior-art, and market records (USPTO, Google Patents, Census, SEC filings, competitor sites) using terms drawn from it. We read those sources; the idea itself is never posted, filed, or shared.',
    flow: { reads: 'patents \u00b7 prior art \u00b7 market data', toward: 'your idea', blocked: 'your idea \u2192 anyone' },
  },
];

// ---------------------------------------------------------------------------
// Cursor-anchored note — fires on focus of an idea-description textarea.
// Split so the middle phrase can carry the brass underline + link.
// ---------------------------------------------------------------------------

export const CURSOR_NOTE = {
  before: 'Your idea stays private. It is never shared, never used to train AI, and ',
  link: 'never seen by anyone',
  after: ' unless you request a practitioner connection.',
};

// ---------------------------------------------------------------------------
// Static mechanism blurb — for surfaces that show a confidentiality line
// before the input (in-app capture pull-quote, the /try reminder).
// ---------------------------------------------------------------------------

export const IDEA_CAPTURE_BLURB =
  'Your idea is stored encrypted. It is never used to train AI, never published, never indexed, and never seen by a human unless you request a practitioner connection. To research it, we search public patent, prior-art, and market records (think Census data, SEC filings, competitor sites) using terms drawn from your idea, but the idea itself never gets posted or shared anywhere.';
